import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/utils/supabase/client';
import { functionsBaseUrl, publicAnonKey, supabaseUrl } from '/utils/supabase/info';
import type {
  AqlCamera,
  AqlClient,
  AqlConfigurationDataset,
  AqlKit,
  AqlLocation,
  AqlPhase,
  AqlProject,
  AqlProjectSetting,
  AqlSensor,
  AqlSpecies,
  AqlUltrasoundDevice,
  AqlUserAccess,
  AqlZone
} from '@/app/types/aql-configuration';
import { normalizeOperationalContextType } from '@/app/utils/operational-context';

type DatasetKey = keyof AqlConfigurationDataset;

export interface ConfigurationPortalAccess {
  email: string;
  authUserId: string;
  accessToken: string;
  role?: string | null;
  clientId?: string | null;
  mode: 'admin' | 'client';
  clientSlug: string | null;
}

export const EMPTY_CONFIGURATION_DATASET: AqlConfigurationDataset = {
  clients: [],
  projects: [],
  locations: [],
  zones: [],
  kits: [],
  cameras: [],
  ultrasoundDevices: [],
  sensors: [],
  species: [],
  phases: [],
  projectSettings: [],
  users: []
};

const tableMap: Record<DatasetKey, string> = {
  clients: 'aql_clients',
  projects: 'aql_projects',
  locations: 'aql_locations',
  zones: 'aql_zones',
  kits: 'aql_kits',
  cameras: 'aql_cameras',
  ultrasoundDevices: 'aql_devices',
  sensors: 'aql_sensors',
  species: 'aql_species',
  phases: 'aql_phases',
  projectSettings: 'aql_project_settings',
  users: 'aql_users'
};

const idKeyMap: Record<DatasetKey, string> = {
  clients: 'client_id',
  projects: 'project_id',
  locations: 'location_id',
  zones: 'zone_id',
  kits: 'kit_id',
  cameras: 'camera_id',
  ultrasoundDevices: 'device_id',
  sensors: 'sensor_id',
  species: 'species_id',
  phases: 'phase_id',
  projectSettings: 'setting_id',
  users: 'user_id'
};

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const liveFeedPathForCameraCode = (code: string | null) =>
  code ? `live/${code}/feed.jpg` : null;

const trainingBurstPathForCameraCode = (code: string | null) =>
  code ? `live/${code}/training` : null;

const pickString = (...values: unknown[]) => {
  const found = values.find((value) => value !== undefined && value !== null && value !== '');
  return found == null ? null : String(found);
};

const pickNumber = (...values: unknown[]) => {
  const found = values.find((value) => value !== undefined && value !== null && value !== '');
  if (found == null) return null;
  const parsed = typeof found === 'number' ? found : Number(String(found));
  return Number.isFinite(parsed) ? parsed : null;
};

const payloadNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeClient = (row: any): AqlClient => ({
  client_id: String(row.client_id),
  name: String(row.name ?? ''),
  slug: String(row.slug ?? ''),
  legal_name: pickString(row.legal_name),
  logo_url: pickString(row.logo_url),
  primary_color: pickString(row.primary_color),
  secondary_color: pickString(row.secondary_color),
  timezone: pickString(row.timezone),
  language: pickString(row.language),
  is_active: Boolean(row.is_active ?? true),
  created_at: pickString(row.created_at) ?? undefined,
  updated_at: pickString(row.updated_at) ?? undefined
});

const normalizeProject = (row: any): AqlProject => ({
  project_id: String(row.project_id),
  client_id: String(row.client_id ?? ''),
  name: String(row.name ?? ''),
  slug: String(row.slug ?? ''),
  description: pickString(row.description),
  cover_image_url: pickString(row.cover_image_url),
  context_type: normalizeOperationalContextType(row.context_type),
  species_focus: pickString(row.species_focus),
  default_phase: pickString(row.default_phase),
  start_date: pickString(row.start_date),
  end_date: pickString(row.end_date),
  is_active: Boolean(row.is_active ?? true),
  created_at: pickString(row.created_at) ?? undefined,
  updated_at: pickString(row.updated_at) ?? undefined
});

const normalizeLocation = (row: any): AqlLocation => ({
  location_id: String(row.location_id),
  client_id: String(row.client_id ?? ''),
  project_id: String(row.project_id ?? ''),
  name: String(row.name ?? row.location_name ?? ''),
  type: String(row.type ?? row.location_type ?? 'site'),
  address: pickString(row.address, row.street, row.address_line),
  city: pickString(row.city),
  country: pickString(row.country),
  postal_code: pickString(row.postal_code),
  latitude: pickNumber(row.latitude),
  longitude: pickNumber(row.longitude),
  description: pickString(row.description),
  is_active: Boolean(row.is_active ?? true),
  created_at: pickString(row.created_at) ?? undefined,
  updated_at: pickString(row.updated_at) ?? undefined
});

const normalizeZone = (row: any): AqlZone => ({
  zone_id: String(row.zone_id),
  location_id: String(row.location_id ?? ''),
  client_id: String(row.client_id ?? ''),
  project_id: String(row.project_id ?? ''),
  name: String(row.name ?? ''),
  code: pickString(row.code),
  type: String(row.type ?? ''),
  species: pickString(row.species),
  phase: pickString(row.phase),
  latitude: pickNumber(row.latitude),
  longitude: pickNumber(row.longitude),
  description: pickString(row.description),
  is_active: Boolean(row.is_active ?? true),
  created_at: pickString(row.created_at) ?? undefined,
  updated_at: pickString(row.updated_at) ?? undefined
});

const normalizeKit = (row: any): AqlKit => ({
  kit_id: String(row.kit_id),
  client_id: String(row.client_id ?? ''),
  location_id: String(row.location_id ?? ''),
  project_id: String(row.project_id ?? ''),
  zone_id: pickString(row.zone_id),
  name: String(row.name ?? ''),
  kit_type: String(row.kit_type ?? row.type ?? 'vision'),
  controller_profile: pickString(row.controller_profile) ?? 'atronia-atlas',
  connectivity_mode: pickString(row.connectivity_mode) ?? 'ethernet',
  offline_buffer_hours: pickNumber(row.offline_buffer_hours) ?? 72,
  bulk_batch_size: pickNumber(row.bulk_batch_size) ?? 500,
  local_alerting_enabled: Boolean(row.local_alerting_enabled ?? true),
  configuration_revision: pickNumber(row.configuration_revision) ?? 1,
  status: String(row.status ?? 'active'),
  heartbeat: pickString(row.last_heartbeat_at, row.heartbeat, row.last_seen_at),
  heartbeat_interval_seconds: pickNumber(row.heartbeat_interval_seconds),
  capture_interval_seconds: pickNumber(row.capture_interval_seconds),
  frame_interval_ms: pickNumber(row.frame_interval_ms),
  storage_bucket: pickString(row.storage_bucket),
  jpg_quality: pickNumber(row.jpg_quality),
  live_frame_path_template: pickString(row.live_frame_path_template),
  vision_lab_project_id: pickString(row.vision_lab_project_id),
  vision_model_policy: row.vision_model_policy === 'pinned' ? 'pinned' : 'latest_approved',
  vision_model_version: pickNumber(row.vision_model_version),
  video_acquisition_enabled: Boolean(row.video_acquisition_enabled ?? true),
  sensor_acquisition_enabled: Boolean(row.sensor_acquisition_enabled ?? true),
  acquisition_updated_at: pickString(row.acquisition_updated_at),
  description: pickString(row.description),
  is_active: Boolean(row.is_active ?? true)
});

const normalizeCamera = (row: any): AqlCamera => ({
  camera_id: String(row.camera_id),
  kit_id: pickString(row.kit_id),
  zone_id: pickString(row.zone_id),
  name: String(row.name ?? ''),
  camera_code: pickString(row.camera_code),
  source_type: pickString(row.source_type),
  source_uri: pickString(row.source_uri),
  live_feed_path: pickString(row.live_feed_path),
  last_frame_at: pickString(row.last_frame_at),
  last_frame_status: pickString(row.last_frame_status),
  capture_interval_seconds: pickNumber(row.capture_interval_seconds),
  frame_interval_ms: pickNumber(row.frame_interval_ms),
  training_burst_available: Boolean(row.training_burst_available ?? false),
  training_burst_enabled: Boolean(row.training_burst_enabled ?? false),
  training_burst_target: pickNumber(row.training_burst_target) ?? 50,
  training_burst_count: pickNumber(row.training_burst_count) ?? 0,
  training_burst_path: pickString(row.training_burst_path),
  training_burst_started_at: pickString(row.training_burst_started_at),
  training_burst_completed_at: pickString(row.training_burst_completed_at),
  resolution: pickString(row.resolution),
  position_description: pickString(row.position_description),
  is_primary: Boolean(row.is_primary ?? false),
  is_active: Boolean(row.is_active ?? true)
});

const normalizeSensor = (row: any): AqlSensor => ({
  sensor_id: String(row.sensor_id),
  client_id: pickString(row.client_id),
  project_id: pickString(row.project_id),
  location_id: pickString(row.location_id),
  kit_id: String(row.kit_id ?? ''),
  zone_id: pickString(row.zone_id),
  device_id: pickString(row.device_id),
  sensor_type: String(row.sensor_type ?? row.type ?? ''),
  name: String(row.name ?? ''),
  unit: pickString(row.unit),
  min_threshold: pickNumber(row.min_threshold),
  max_threshold: pickNumber(row.max_threshold),
  recommended_min: pickNumber(row.recommended_min),
  recommended_max: pickNumber(row.recommended_max),
  last_value: pickNumber(row.last_value),
  is_active: Boolean(row.is_active ?? true)
});

const normalizeUltrasoundDevice = (row: any): AqlUltrasoundDevice => ({
  device_id: String(row.device_id),
  client_id: String(row.client_id ?? ''),
  kit_id: String(row.kit_id ?? ''),
  name: String(row.name ?? ''),
  device_type: 'ultrasound',
  status: String(row.status ?? 'active'),
  description: pickString(row.description),
  is_active: Boolean(row.is_active ?? true),
  created_at: pickString(row.created_at) ?? undefined,
  updated_at: pickString(row.updated_at) ?? undefined
});

const normalizeSpecies = (row: any): AqlSpecies => ({
  species_id: String(row.species_id),
  client_id: String(row.client_id ?? ''),
  project_id: String(row.project_id ?? ''),
  common_name: String(row.common_name ?? ''),
  scientific_name: pickString(row.scientific_name),
  code: pickString(row.code),
  is_active: Boolean(row.is_active ?? true)
});

const normalizePhase = (row: any): AqlPhase => ({
  phase_id: String(row.phase_id),
  client_id: String(row.client_id ?? ''),
  project_id: String(row.project_id ?? ''),
  name: String(row.name ?? ''),
  code: pickString(row.code),
  sort_order: pickNumber(row.sort_order),
  is_active: Boolean(row.is_active ?? true)
});

const normalizeProjectSetting = (row: any): AqlProjectSetting => ({
  setting_id: String(row.setting_id),
  client_id: String(row.client_id ?? ''),
  project_id: String(row.project_id ?? ''),
  sensor_id: pickString(row.sensor_id, row.value?.sensor_id, row.value?.sensorId, row.value?.source_sensor_id),
  key: String(row.key ?? ''),
  value: typeof row.value === 'object' && row.value !== null ? row.value : {}
});

const normalizeUser = (row: any): AqlUserAccess => ({
  user_id: String(row.user_id),
  auth_user_id: pickString(row.auth_user_id),
  client_id: pickString(row.client_id),
  client_ids: Array.isArray(row.client_ids) ? row.client_ids.map(String).filter(Boolean) : [],
  project_id: pickString(row.project_id),
  project_ids: Array.isArray(row.project_ids) ? row.project_ids.map(String).filter(Boolean) : [],
  project_scope: (Array.isArray(row.project_ids) && row.project_ids.length > 0) || row.project_id ? 'specific' : 'all',
  name: String(row.full_name ?? row.user_name ?? row.username ?? ''),
  job_title: pickString(row.job_title, row.jobTitle),
  username: pickString(row.username, row.user_name),
  email: String(row.email ?? ''),
  mobile_phone: pickString(row.user_phone, row.mobile_phone),
  alert_notifications_enabled: Boolean(row.alert_notifications_enabled ?? true),
  role: String(row.role ?? 'viewer'),
  is_active: Boolean(row.is_active ?? true),
  last_login: pickString(row.last_login)
});

const normalizers: Record<DatasetKey, (row: any) => any> = {
  clients: normalizeClient,
  projects: normalizeProject,
  locations: normalizeLocation,
  zones: normalizeZone,
  kits: normalizeKit,
  cameras: normalizeCamera,
  ultrasoundDevices: normalizeUltrasoundDevice,
  sensors: normalizeSensor,
  species: normalizeSpecies,
  phases: normalizePhase,
  projectSettings: normalizeProjectSetting,
  users: normalizeUser
};

async function ensureCameraLiveFolder(camera: AqlCamera) {
  const cameraCode = String(camera.camera_code ?? '').trim();
  const cameraId = String(camera.camera_id ?? '').trim();
  const liveFeedPath = String(camera.live_feed_path ?? '').trim();
  if (!cameraCode && !cameraId && !liveFeedPath) return;

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) return;

  const response = await fetch(`${functionsBaseUrl}/vision-captures/live-folder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: publicAnonKey,
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      camera_id: cameraId || null,
      camera_code: cameraCode || null,
      live_feed_path: liveFeedPath || null,
      create_training_folder: Boolean(camera.training_burst_available),
      training_burst_path: String(camera.training_burst_path ?? '').trim() || null
    })
  });

  if (!response.ok) {
    const responseText = await response.text().catch(() => '');
    throw new Error(responseText || `Could not create live camera folder. HTTP ${response.status}`);
  }
}

const serializePayload = <K extends DatasetKey>(
  key: K,
  item: AqlConfigurationDataset[K][number]
): Record<string, any> => {
  const payload = { ...(item as Record<string, any>) };
  Object.keys(payload).forEach((key) => {
    if (key.startsWith('__')) delete payload[key];
  });

  if (key === 'users') {
    const clientIds = Array.from(new Set([
      ...(Array.isArray(payload.client_ids) ? payload.client_ids : [])
        .map((value) => String(value ?? '').trim())
        .filter(Boolean),
      ...(payload.client_id ? [String(payload.client_id)] : [])
    ]));
    const projectIds = Array.from(new Set(
      (Array.isArray(payload.project_ids) ? payload.project_ids : [])
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    ));
    const projectScope = payload.project_scope === 'specific'
      ? 'specific'
      : projectIds.length || payload.project_id
        ? 'specific'
        : 'all';
    const scopedProjectIds = projectScope === 'specific'
      ? projectIds.length
        ? projectIds
        : payload.project_id
          ? [String(payload.project_id)]
          : []
      : [];

    return {
      user_id: payload.user_id,
      auth_user_id: payload.auth_user_id || null,
      client_id: clientIds[0] || null,
      client_ids: clientIds,
      project_id: scopedProjectIds[0] || null,
      project_ids: scopedProjectIds,
      project_scope: projectScope,
      full_name: payload.name,
      user_name: payload.name,
      job_title: payload.job_title || null,
      username: payload.username || payload.email?.split('@')[0] || null,
      email: payload.email,
      mobile_phone: payload.mobile_phone || null,
      user_phone: payload.mobile_phone || null,
      alert_notifications_enabled: payload.alert_notifications_enabled !== false,
      role: payload.role,
      is_active: payload.is_active,
      last_login: payload.last_login || null
    };
  }

  if (key === 'clients') {
    delete payload.type;
    return {
      ...payload,
      slug: slugify(String(payload.slug || payload.name || ''))
    };
  }

  if (key === 'projects') {
    delete payload.type;
    return {
      ...payload,
      slug: slugify(String(payload.slug || payload.name || ''))
    };
  }

  if (key === 'projectSettings') {
    const rawValue = payload.value;
    let value = rawValue;
    if (typeof rawValue === 'string') {
      try {
        value = JSON.parse(rawValue);
      } catch {
        value = { text: rawValue };
      }
    }
    const normalizedValue = typeof value === 'object' && value !== null ? { ...value } : {};
    if (payload.sensor_id) {
      normalizedValue.sensor_id = String(payload.sensor_id);
    }

    return {
      setting_id: payload.setting_id,
      client_id: payload.client_id,
      project_id: payload.project_id,
      key: payload.key,
      value: normalizedValue
    };
  }

  if (key === 'locations') {
    const latitude = payloadNumber(payload.latitude);
    let longitude = payloadNumber(payload.longitude);
    const isPortugal = String(payload.country ?? '').toLowerCase().includes('portugal');
    if (
      isPortugal &&
      latitude != null &&
      longitude != null &&
      latitude >= 36.8 &&
      latitude <= 42.3 &&
      longitude > 0 &&
      longitude <= 10
    ) {
      longitude = -longitude;
    }

    return {
      location_id: payload.location_id,
      client_id: payload.client_id,
      project_id: payload.project_id || null,
      name: payload.name,
      type: payload.type || 'site',
      address: payload.address || null,
      city: payload.city || null,
      country: payload.country || null,
      postal_code: payload.postal_code || null,
      latitude,
      longitude,
      description: payload.description || null,
      is_active: payload.is_active
    };
  }

  if (key === 'zones') {
    return {
      zone_id: payload.zone_id,
      client_id: payload.client_id,
      project_id: payload.project_id || null,
      location_id: payload.location_id,
      name: payload.name,
      code: payload.code || null,
      type: payload.type || 'tank',
      species: payload.species || null,
      phase: payload.phase || null,
      latitude: payload.latitude == null || payload.latitude === '' ? null : Number(payload.latitude),
      longitude: payload.longitude == null || payload.longitude === '' ? null : Number(payload.longitude),
      description: payload.description || null,
      is_active: payload.is_active
    };
  }

  if (key === 'kits') {
    return {
      kit_id: payload.kit_id,
      client_id: payload.client_id,
      location_id: payload.location_id,
      project_id: payload.project_id || null,
      zone_id: payload.zone_id || null,
      name: payload.name,
      kit_type: payload.kit_type,
      controller_profile: payload.controller_profile || 'atronia-atlas',
      connectivity_mode: payload.connectivity_mode || 'ethernet',
      offline_buffer_hours: payload.offline_buffer_hours == null ? 72 : Number(payload.offline_buffer_hours),
      bulk_batch_size: payload.bulk_batch_size == null ? 500 : Number(payload.bulk_batch_size),
      local_alerting_enabled: payload.local_alerting_enabled !== false,
      configuration_revision: payload.configuration_revision == null ? 1 : Number(payload.configuration_revision),
      status: payload.status,
      description: payload.description || null,
      last_heartbeat_at: payload.heartbeat || null,
      capture_interval_seconds: payload.capture_interval_seconds ?? null,
      frame_interval_ms: payload.frame_interval_ms ?? null,
      is_active: payload.is_active
    };
  }

  if (key === 'cameras') {
    const cameraCode = slugify(String(payload.camera_code || payload.name || payload.camera_id || '')).slice(0, 80);
    const liveFeedPath = liveFeedPathForCameraCode(cameraCode);
    const trainingBurstPath = payload.training_burst_path || trainingBurstPathForCameraCode(cameraCode);

    return {
      camera_id: payload.camera_id,
      kit_id: payload.kit_id || null,
      zone_id: payload.zone_id || null,
      name: payload.name,
      camera_code: cameraCode || null,
      source_type: payload.source_type || 'storage',
      source_uri: payload.source_uri || liveFeedPath,
      live_feed_path: liveFeedPath,
      last_frame_at: payload.last_frame_at || null,
      last_frame_status: payload.last_frame_status || null,
      capture_interval_seconds: payload.capture_interval_seconds ?? null,
      frame_interval_ms: payload.frame_interval_ms ?? null,
      training_burst_available: Boolean(payload.training_burst_available),
      training_burst_enabled: false,
      training_burst_target: payloadNumber(payload.training_burst_target) ?? 50,
      training_burst_count: 0,
      training_burst_path: trainingBurstPath || null,
      training_burst_started_at: null,
      training_burst_completed_at: null,
      resolution: payload.resolution || null,
      position_description: payload.position_description || null,
      is_primary: Boolean(payload.is_primary),
      is_active: payload.is_active
    };
  }

  if (key === 'ultrasoundDevices') {
    return {
      device_id: payload.device_id,
      client_id: payload.client_id,
      kit_id: payload.kit_id,
      name: payload.name,
      device_type: 'ultrasound',
      status: payload.status || 'active',
      description: payload.description || null,
      is_active: payload.is_active
    };
  }

  return payload;
};

const sortRows = <T extends Record<string, any>>(rows: T[], key: string) =>
  [...rows].sort((a, b) => String(a[key] ?? '').localeCompare(String(b[key] ?? '')));

const QUERY_TIMEOUT_MS = 15000;

const missingColumnError = (error: unknown, column: string) => {
  const message = String((error as { message?: string })?.message ?? error ?? '');
  return message.includes(`'${column}'`) && /column|schema cache/i.test(message);
};

const missingRelationError = (error: unknown, relation: string) => {
  const message = String((error as { message?: string })?.message ?? error ?? '');
  return message.includes(relation) && /schema cache|could not find|does not exist|relation/i.test(message);
};

const getCurrentAuthRedirectUrl = () => {
  if (typeof window === 'undefined') return undefined;
  if (!window.location.origin || window.location.origin === 'null') return undefined;
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
};

async function withQueryTimeout<T>(run: (signal: AbortSignal) => Promise<T>, message: string): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = globalThis.setTimeout(() => {
      controller.abort();
      reject(new Error(message));
    }, QUERY_TIMEOUT_MS);
  });

  try {
    return await Promise.race([run(controller.signal), timeout]);
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      throw new Error(message);
    }
    throw error;
  } finally {
    if (timer) globalThis.clearTimeout(timer);
  }
}

type ConfigurationDb = typeof supabase;

const configurationClient = (accessToken?: string | null): ConfigurationDb => {
  const token = String(accessToken ?? '').trim();
  if (!token || !supabaseUrl || !publicAnonKey) return supabase;

  return createClient(supabaseUrl, publicAnonKey, {
    auth: {
      autoRefreshToken: false,
      detectSessionInUrl: false,
      persistSession: false
    },
    global: {
      headers: { Authorization: `Bearer ${token}` }
    }
  }) as ConfigurationDb;
};

async function fetchTable<K extends DatasetKey>(key: K, db: ConfigurationDb = supabase): Promise<AqlConfigurationDataset[K]> {
  const table = tableMap[key];
  const { data, error } = await withQueryTimeout(
    (signal) => {
      let query = db.from(table).select('*');
      if (key === 'ultrasoundDevices') query = query.eq('device_type', 'ultrasound');
      return query.abortSignal(signal);
    },
    `Timed out loading ${table}.`
  );
  if (error) throw error;
  const normalized = (data ?? []).map((row) => normalizers[key](row));
  const sortKey = key === 'projectSettings' ? 'key' : key === 'users' ? 'name' : key === 'phases' ? 'sort_order' : 'name';
  return sortRows(normalized, sortKey) as AqlConfigurationDataset[K];
}

async function fetchUserProjectAccess(db: ConfigurationDb = supabase): Promise<Record<string, string[]>> {
  const { data, error } = await withQueryTimeout(
    (signal) => db.from('aql_user_projects').select('user_id,project_id').abortSignal(signal),
    'Timed out loading aql_user_projects.'
  );
  if (error) throw error;

  return (data ?? []).reduce<Record<string, string[]>>((acc, row: any) => {
    const userId = String(row.user_id ?? '').trim();
    const projectId = String(row.project_id ?? '').trim();
    if (!userId || !projectId) return acc;
    acc[userId] = Array.from(new Set([...(acc[userId] ?? []), projectId]));
    return acc;
  }, {});
}

async function fetchUserClientAccess(db: ConfigurationDb = supabase): Promise<Record<string, string[]>> {
  const { data, error } = await withQueryTimeout(
    (signal) => db.from('aql_user_clients').select('user_id,client_id').abortSignal(signal),
    'Timed out loading aql_user_clients.'
  );
  if (error) throw error;

  return (data ?? []).reduce<Record<string, string[]>>((acc, row: any) => {
    const userId = String(row.user_id ?? '').trim();
    const clientId = String(row.client_id ?? '').trim();
    if (!userId || !clientId) return acc;
    acc[userId] = Array.from(new Set([...(acc[userId] ?? []), clientId]));
    return acc;
  }, {});
}

const runtimeConfigValue = (value: unknown) => {
  if (value && typeof value === 'object') return value;
  return { value };
};

async function saveRuntimeConfigs(key: DatasetKey, payload: Record<string, any>) {
  if (key !== 'kits' && key !== 'cameras') return;

  const clientId = String(payload.client_id ?? '').trim();
  if (!clientId) return;

  const cameraPrefix = key === 'cameras' && payload.camera_code ? `camera.${payload.camera_code}.` : '';
  const runtimeKeys = key === 'kits'
    ? ['capture_interval_seconds', 'frame_interval_ms', 'storage_bucket', 'jpg_quality', 'live_frame_path_template']
    : ['capture_interval_seconds', 'frame_interval_ms', 'storage_bucket', 'live_feed_path'];
  const runtimeRows = runtimeKeys
    .filter((configKey) => payload[configKey] !== undefined && payload[configKey] !== null && payload[configKey] !== '')
    .map((configKey) => ({
    client_id: clientId,
    project_id: payload.project_id || null,
    location_id: payload.location_id || null,
    zone_id: payload.zone_id || null,
    kit_id: key === 'kits' ? payload.kit_id : payload.kit_id || null,
    camera_id: key === 'cameras' ? payload.camera_id : null,
    device_id: null,
    config_key: `${cameraPrefix}${configKey}`,
    config_value: runtimeConfigValue(payload[configKey]),
    is_enabled: true
  }));
  if (!runtimeRows.length) return;

  const { error } = await withQueryTimeout(
    (signal) => supabase
      .from('aql_device_runtime_configs')
      .upsert(runtimeRows, {
        onConflict: 'client_id,project_id,location_id,zone_id,kit_id,device_id,camera_id,config_key'
      })
      .abortSignal(signal),
    'Timed out while saving live runtime config.'
  );

  if (error) {
    console.warn('AQL Vision: could not save edge runtime config', error);
  }
}

export async function loadConfigurationDataset(accessToken?: string | null): Promise<{ data: AqlConfigurationDataset; source: 'supabase' | 'mixed'; warning?: string }> {
  const keys = Object.keys(tableMap) as DatasetKey[];
  const warnings: string[] = [];
  const result = { ...EMPTY_CONFIGURATION_DATASET } as AqlConfigurationDataset;
  const db = configurationClient(accessToken);
  let supabaseCount = 0;

  await Promise.all(
    keys.map(async (key) => {
      try {
        result[key] = await fetchTable(key, db);
        supabaseCount += 1;
      } catch (error: any) {
        warnings.push(`${tableMap[key]}: ${error?.message ?? 'unavailable'}`);
      }
    })
  );

  try {
    const { data: runtimeRows, error: runtimeError } = await withQueryTimeout(
      (signal) => db
        .from('aql_device_runtime_configs')
        .select('kit_id,camera_id,config_key,config_value,is_enabled,updated_at')
        .eq('is_enabled', true)
        .order('updated_at', { ascending: true })
        .abortSignal(signal),
      'Timed out loading edge runtime configuration.'
    );
    if (runtimeError) throw runtimeError;

    const unwrap = (value: unknown) => {
      if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
        return (value as Record<string, unknown>).value;
      }
      return value;
    };
    for (const row of runtimeRows ?? []) {
      const kitId = String(row.kit_id ?? '');
      const cameraId = String(row.camera_id ?? '');
      const configKey = String(row.config_key ?? '');
      const value = unwrap(row.config_value);
      if (kitId && !cameraId) {
        const kit = result.kits.find((item) => item.kit_id === kitId) as Record<string, any> | undefined;
        if (kit && ['heartbeat_interval_seconds', 'capture_interval_seconds', 'frame_interval_ms', 'storage_bucket', 'jpg_quality', 'live_frame_path_template'].includes(configKey)) {
          kit[configKey] = value;
        }
      }
      if (cameraId) {
        const camera = result.cameras.find((item) => item.camera_id === cameraId) as Record<string, any> | undefined;
        const localKey = configKey.replace(/^camera\.[^.]+\./, '');
        if (camera && ['capture_interval_seconds', 'frame_interval_ms', 'storage_bucket', 'live_feed_path'].includes(localKey)) {
          camera[localKey] = value;
        }
      }
    }
  } catch (error: any) {
    if (!missingRelationError(error, 'aql_device_runtime_configs')) {
      warnings.push(`aql_device_runtime_configs: ${error?.message ?? 'unavailable'}`);
    }
  }

  if (result.users.length) {
    try {
      const clientAccess = await fetchUserClientAccess(db);
      result.users = result.users.map((user) => {
        const linkedClientIds = clientAccess[user.user_id] ?? [];
        const clientIds = Array.from(new Set([
          ...linkedClientIds,
          ...(user.client_id ? [user.client_id] : [])
        ]));
        return {
          ...user,
          client_ids: clientIds,
          client_id: user.client_id ?? clientIds[0] ?? null
        };
      });
    } catch (error: any) {
      if (!missingRelationError(error, 'aql_user_clients')) {
        console.warn('AQL Vision: could not load user client access', error);
      }
    }

    try {
      const projectAccess = await fetchUserProjectAccess(db);
      result.users = result.users.map((user) => {
        const linkedProjectIds = projectAccess[user.user_id] ?? [];
        const projectIds = Array.from(new Set([
          ...linkedProjectIds,
          ...(user.project_id ? [user.project_id] : [])
        ]));
        return {
          ...user,
          project_ids: projectIds,
          project_id: projectIds[0] ?? null,
          project_scope: projectIds.length ? 'specific' : 'all'
        };
      });
    } catch (error: any) {
      if (!missingRelationError(error, 'aql_user_projects')) {
        console.warn('AQL Vision: could not load user project access', error);
      }
    }
  }

  const source = supabaseCount === keys.length ? 'supabase' : 'mixed';

  return {
    data: result,
    source,
    warning: warnings.length ? warnings.join(' | ') : undefined
  };
}

export async function saveConfigurationRecord<K extends DatasetKey>(
  key: K,
  item: AqlConfigurationDataset[K][number]
): Promise<AqlConfigurationDataset[K][number]> {
  const table = tableMap[key];
  const idKey = idKeyMap[key];
  const requestedRuntime = { ...(item as Record<string, any>) };
  let payload = serializePayload(key, item);
  const requestedFrameIntervalMs = (payload as Record<string, any>).frame_interval_ms;
  const idValue = String((payload as Record<string, any>)[idKey] ?? '');

  let { error: writeError } = await withQueryTimeout(
    (signal) => supabase.from(table).upsert(payload, { onConflict: idKey }).abortSignal(signal),
    `Timed out while saving ${table}.`
  );

  if (writeError && missingColumnError(writeError, 'frame_interval_ms') && (key === 'kits' || key === 'cameras')) {
    const retryPayload = { ...(payload as Record<string, any>) };
    delete retryPayload.frame_interval_ms;
    payload = retryPayload as typeof payload;
    const retry = await withQueryTimeout(
      (signal) => supabase.from(table).upsert(payload, { onConflict: idKey }).abortSignal(signal),
      `Timed out while saving ${table}.`
    );
    writeError = retry.error;
  }

  if (writeError) throw writeError;

  if (key === 'kits' || key === 'cameras') {
    await saveRuntimeConfigs(key, {
      ...requestedRuntime,
      ...(payload as Record<string, any>),
      frame_interval_ms: requestedFrameIntervalMs
    });
  }

  const { data, error: readError } = await withQueryTimeout(
    (signal) => supabase.from(table).select('*').eq(idKey, idValue).abortSignal(signal).maybeSingle(),
    `Timed out while loading the saved ${table} record.`
  );

  if (readError) throw readError;
  if (!data) {
    throw new Error(`Saved ${table}, but could not read the record back. Check table policies for select access.`);
  }

  const saved = normalizers[key](data) as AqlConfigurationDataset[K][number];
  if ((key === 'kits' || key === 'cameras') && Number.isFinite(Number(requestedFrameIntervalMs))) {
    (saved as Record<string, any>).frame_interval_ms = Number(requestedFrameIntervalMs);
  }
  if (key === 'kits' || key === 'cameras') {
    Object.assign(saved as Record<string, any>, requestedRuntime);
  }
  if (key === 'cameras') {
    await ensureCameraLiveFolder(saved as AqlCamera).catch((error) => {
      console.warn('AQL Vision: could not create live camera folder', error);
    });
  }

  return saved;
}

export async function saveConfigurationUserAccess(
  item: AqlUserAccess,
  mode: 'create' | 'edit',
  access: ConfigurationPortalAccess
): Promise<AqlUserAccess> {
  const email = String(item.email ?? '').trim().toLowerCase();
  const mobilePhone = String(item.mobile_phone ?? '').replace(/[\s().-]/g, '');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error('Please enter a valid email address before creating the user.');
  }
  if (mobilePhone && !/^\+[1-9]\d{7,14}$/.test(mobilePhone)) {
    throw new Error('Please enter the mobile phone in international format, for example +351912345678.');
  }

  try {
    const response = await fetch(`${functionsBaseUrl}/config-manage-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: publicAnonKey,
        Authorization: `Bearer ${access.accessToken}`
      },
      body: JSON.stringify({
        operation: mode,
        accessMode: access.mode,
        clientSlug: access.clientSlug,
        redirectTo: getCurrentAuthRedirectUrl(),
        user: serializePayload('users', { ...item, email, mobile_phone: mobilePhone || null })
      })
    });

    const responseText = await response.text();
    let payload: any = {};
    try {
      payload = responseText ? JSON.parse(responseText) : {};
    } catch {
      payload = {};
    }
    if (!response.ok) {
      throw new Error(payload?.error ?? (responseText || `Failed to provision user. HTTP ${response.status}`));
    }

    return normalizeUser(payload.user);
  } catch (error: any) {
    if (error instanceof TypeError) {
      throw new Error('Could not reach the user provisioning function. Check that the Supabase Edge Function `config-manage-user` is deployed and reachable.');
    }
    throw error;
  }
}

export async function deleteConfigurationRecord<K extends DatasetKey>(
  key: K,
  item: AqlConfigurationDataset[K][number]
): Promise<void> {
  const table = tableMap[key];
  const idKey = idKeyMap[key];
  const idValue = (item as Record<string, any>)[idKey];
  const { error } = await supabase.from(table).delete().eq(idKey, idValue);
  if (error) throw error;
}

const asIdList = (rows: any[] | null | undefined, key: string) =>
  Array.from(new Set((rows ?? []).map((row) => row?.[key]).filter(Boolean).map(String)));

const selectIds = async (table: string, idKey: string, column: string, value: string) => {
  const { data, error } = await supabase.from(table).select(idKey).eq(column, value);
  if (error) throw error;
  return asIdList(data as any[], idKey);
};

const selectIdsIn = async (table: string, idKey: string, column: string, values: string[]) => {
  if (values.length === 0) return [];
  const { data, error } = await supabase.from(table).select(idKey).in(column, values);
  if (error) throw error;
  return asIdList(data as any[], idKey);
};

const deleteEq = async (table: string, column: string, value: string) => {
  const { error } = await supabase.from(table).delete().eq(column, value);
  if (error) throw error;
};

const deleteIn = async (table: string, column: string, values: string[]) => {
  if (values.length === 0) return;
  const { error } = await supabase.from(table).delete().in(column, values);
  if (error) throw error;
};

const clearUserProjectFallback = async (projectId: string) => {
  const { error } = await supabase
    .from('aql_users')
    .update({ project_id: null })
    .eq('project_id', projectId);
  if (error) throw error;
};

const isMissingRpcError = (error: any) =>
  error?.code === 'PGRST202' ||
  String(error?.message ?? '').toLowerCase().includes('could not find the function');

const deleteOperationalCascade = async ({
  locationIds = [],
  zoneIds = [],
  kitIds = [],
  cameraIds = [],
  sensorIds = [],
  deviceIds = []
}: {
  locationIds?: string[];
  zoneIds?: string[];
  kitIds?: string[];
  cameraIds?: string[];
  sensorIds?: string[];
  deviceIds?: string[];
}) => {
  const zoneIdsFromLocations = await selectIdsIn('aql_zones', 'zone_id', 'location_id', locationIds);
  const allZoneIds = Array.from(new Set([...zoneIds, ...zoneIdsFromLocations]));

  const [kitIdsFromLocations, kitIdsFromZones] = await Promise.all([
    selectIdsIn('aql_kits', 'kit_id', 'location_id', locationIds),
    selectIdsIn('aql_kits', 'kit_id', 'zone_id', allZoneIds)
  ]);
  const allKitIds = Array.from(new Set([...kitIds, ...kitIdsFromLocations, ...kitIdsFromZones]));

  const [cameraIdsByKit, cameraIdsByZone, sensorIdsByKit, sensorIdsByZone, deviceIdsByKit] = await Promise.all([
    selectIdsIn('aql_cameras', 'camera_id', 'kit_id', allKitIds),
    selectIdsIn('aql_cameras', 'camera_id', 'zone_id', allZoneIds),
    selectIdsIn('aql_sensors', 'sensor_id', 'kit_id', allKitIds),
    selectIdsIn('aql_sensors', 'sensor_id', 'zone_id', allZoneIds),
    selectIdsIn('aql_devices', 'device_id', 'kit_id', allKitIds)
  ]);
  const allCameraIds = Array.from(new Set([...cameraIds, ...cameraIdsByKit, ...cameraIdsByZone]));
  const allSensorIds = Array.from(new Set([...sensorIds, ...sensorIdsByKit, ...sensorIdsByZone]));
  const sensorDeviceIds = await selectIdsIn('aql_sensors', 'device_id', 'sensor_id', allSensorIds);
  const allDeviceIds = Array.from(new Set([...deviceIds, ...deviceIdsByKit, ...sensorDeviceIds]));
  const captureIds = await selectIdsIn('aql_vision_captures', 'capture_id', 'device_id', allDeviceIds);
  const detectionIds = await selectIdsIn('aql_detections', 'detection_id', 'capture_id', captureIds);
  const [alertIdsByDevice, alertIdsByCamera, alertIdsByZone] = await Promise.all([
    selectIdsIn('aql_alerts', 'alert_id', 'device_id', allDeviceIds),
    selectIdsIn('aql_alerts', 'alert_id', 'camera_id', allCameraIds),
    selectIdsIn('aql_alerts', 'alert_id', 'zone_id', allZoneIds)
  ]);
  const alertIds = Array.from(new Set([...alertIdsByDevice, ...alertIdsByCamera, ...alertIdsByZone]));

  await deleteIn('aql_cases', 'detection_id', detectionIds);
  await deleteIn('aql_cases', 'source_capture_id', captureIds);
  await deleteIn('aql_cases', 'alert_id', alertIds);
  await deleteIn('aql_growth_measurements', 'capture_id', captureIds);
  await deleteIn('aql_growth_measurements', 'detection_id', detectionIds);
  await deleteIn('aql_growth_calibrations', 'kit_id', allKitIds);
  await deleteIn('aql_growth_calibrations', 'camera_id', allCameraIds);
  await deleteIn('aql_growth_calibrations', 'device_id', allDeviceIds);
  await deleteIn('aql_detections', 'detection_id', detectionIds);
  await deleteIn('aql_vision_captures', 'capture_id', captureIds);
  await deleteIn('aql_sensor_readings', 'device_id', allDeviceIds);
  await deleteIn('aql_sensor_readings', 'sensor_id', allSensorIds).catch(() => undefined);
  await deleteIn('aql_alerts', 'alert_id', alertIds);
  await deleteIn('aql_sensors', 'sensor_id', allSensorIds);
  await deleteIn('aql_devices', 'device_id', allDeviceIds);
  await deleteIn('aql_cameras', 'camera_id', allCameraIds);
  await deleteIn('aql_kits', 'kit_id', allKitIds);
  await deleteIn('aql_zones', 'zone_id', allZoneIds);
  await deleteIn('aql_locations', 'location_id', locationIds);
};

export async function deleteLocationCascade(location: AqlLocation): Promise<void> {
  if (!location.location_id) throw new Error('Location id is required before deleting a location.');
  await deleteOperationalCascade({ locationIds: [location.location_id] });
}

export async function deleteZoneCascade(zone: AqlZone): Promise<void> {
  if (!zone.zone_id) throw new Error('Zone id is required before deleting a zone.');
  await deleteOperationalCascade({ zoneIds: [zone.zone_id] });
}

export async function deleteKitCascade(kit: AqlKit): Promise<void> {
  if (!kit.kit_id) throw new Error('Kit id is required before deleting a kit.');
  await deleteOperationalCascade({ kitIds: [kit.kit_id] });
}

export async function deleteCameraCascade(camera: AqlCamera): Promise<void> {
  if (!camera.camera_id) throw new Error('Camera id is required before deleting a camera.');
  await deleteOperationalCascade({ cameraIds: [camera.camera_id] });
}

export async function deleteSensorCascade(sensor: AqlSensor): Promise<void> {
  if (!sensor.sensor_id) throw new Error('Sensor id is required before deleting a sensor.');
  await deleteOperationalCascade({ sensorIds: [sensor.sensor_id] });
}

export async function deleteUltrasoundDeviceCascade(device: AqlUltrasoundDevice): Promise<void> {
  if (!device.device_id) throw new Error('Device id is required before deleting a device.');
  await deleteOperationalCascade({ deviceIds: [device.device_id] });
}

export async function deleteProjectCascade(project: AqlProject): Promise<void> {
  const projectId = project.project_id;
  if (!projectId) throw new Error('Project id is required before deleting a project.');

  const rpcResult = await supabase.rpc('aql_delete_project_cascade', { p_project_id: projectId });
  if (!rpcResult.error) return;
  if (!isMissingRpcError(rpcResult.error)) throw rpcResult.error;

  const [
    locationIds,
    zoneIds,
    kitIds,
    directCaseIds,
    directAlertIds,
    directCaptureIds,
    cohortIds
  ] = await Promise.all([
    selectIds('aql_locations', 'location_id', 'project_id', projectId),
    selectIds('aql_zones', 'zone_id', 'project_id', projectId),
    selectIds('aql_kits', 'kit_id', 'project_id', projectId),
    selectIds('aql_cases', 'case_id', 'project_id', projectId),
    selectIds('aql_alerts', 'alert_id', 'project_id', projectId),
    selectIds('aql_vision_captures', 'capture_id', 'metadata->>project_id', projectId).catch(() => []),
    selectIds('aql_cohorts', 'cohort_id', 'project_id', projectId)
  ]);

  const kitIdsFromLocations = await selectIdsIn('aql_kits', 'kit_id', 'location_id', locationIds);
  const allKitIds = Array.from(new Set([...kitIds, ...kitIdsFromLocations]));
  const [cameraIdsByKit, cameraIdsByZone, sensorIds, deviceIds] = await Promise.all([
    selectIdsIn('aql_cameras', 'camera_id', 'kit_id', allKitIds),
    selectIdsIn('aql_cameras', 'camera_id', 'zone_id', zoneIds),
    selectIdsIn('aql_sensors', 'sensor_id', 'kit_id', allKitIds),
    selectIdsIn('aql_devices', 'device_id', 'kit_id', allKitIds)
  ]);
  const cameraIds = Array.from(new Set([...cameraIdsByKit, ...cameraIdsByZone]));
  const sensorDeviceIds = await selectIdsIn('aql_sensors', 'device_id', 'sensor_id', sensorIds);
  const allDeviceIds = Array.from(new Set([...deviceIds, ...sensorDeviceIds]));
  const captureIdsByDevice = await selectIdsIn('aql_vision_captures', 'capture_id', 'device_id', allDeviceIds);
  const captureIds = Array.from(new Set([...directCaptureIds, ...captureIdsByDevice]));
  const detectionIds = await selectIdsIn('aql_detections', 'detection_id', 'capture_id', captureIds);

  await deleteIn('aql_cases', 'detection_id', detectionIds);
  await deleteIn('aql_cases', 'source_capture_id', captureIds);
  await deleteIn('aql_cases', 'alert_id', directAlertIds);
  await deleteIn('aql_cases', 'case_id', directCaseIds);
  await deleteEq('aql_cases', 'project_id', projectId);

  await deleteEq('aql_growth_measurements', 'project_id', projectId);
  await deleteIn('aql_growth_measurements', 'cohort_id', cohortIds);
  await deleteIn('aql_growth_measurements', 'capture_id', captureIds);
  await deleteIn('aql_growth_measurements', 'detection_id', detectionIds);
  await deleteEq('aql_growth_calibrations', 'project_id', projectId);
  await deleteIn('aql_growth_calibrations', 'kit_id', allKitIds);
  await deleteIn('aql_growth_calibrations', 'camera_id', cameraIds);
  await deleteIn('aql_growth_calibrations', 'device_id', allDeviceIds);
  await deleteEq('aql_cohorts', 'project_id', projectId);

  await deleteEq('aql_biomass_metrics', 'project_id', projectId);
  await deleteEq('aql_growth_metrics', 'project_id', projectId);
  await deleteEq('aql_growth_expected_curves', 'project_id', projectId);

  await deleteIn('aql_detections', 'detection_id', detectionIds);
  await deleteIn('aql_detections', 'capture_id', captureIds);
  await deleteIn('aql_vision_captures', 'capture_id', captureIds);
  await deleteIn('aql_sensor_readings', 'device_id', allDeviceIds);
  await deleteEq('aql_alerts', 'project_id', projectId);
  await deleteIn('aql_alerts', 'device_id', allDeviceIds);
  await deleteIn('aql_alerts', 'camera_id', cameraIds);
  await deleteIn('aql_alerts', 'zone_id', zoneIds);

  await deleteEq('aql_user_projects', 'project_id', projectId);
  await deleteEq('aql_project_settings', 'project_id', projectId);
  await clearUserProjectFallback(projectId);
  await deleteEq('aql_species', 'project_id', projectId);
  await deleteEq('aql_phases', 'project_id', projectId);
  await deleteIn('aql_sensors', 'sensor_id', sensorIds);
  await deleteIn('aql_devices', 'device_id', allDeviceIds);
  await deleteIn('aql_cameras', 'camera_id', cameraIds);
  await deleteIn('aql_kits', 'kit_id', allKitIds);
  await deleteIn('aql_zones', 'zone_id', zoneIds);
  await deleteIn('aql_locations', 'location_id', locationIds);

  await deleteConfigurationRecord('projects', project);
}

export async function deleteClientCascade(client: AqlClient): Promise<void> {
  const clientId = client.client_id;
  if (!clientId) throw new Error('Client id is required before deleting a client.');

  const rpcResult = await supabase.rpc('aql_delete_client_cascade', { p_client_id: clientId });
  if (!rpcResult.error) return;
  if (!isMissingRpcError(rpcResult.error)) throw rpcResult.error;

  const projectIds = await selectIds('aql_projects', 'project_id', 'client_id', clientId);
  for (const projectId of projectIds) {
    await deleteProjectCascade({
      project_id: projectId,
      client_id: clientId,
      name: '',
      slug: '',
      is_active: true
    });
  }

  const [locationIds, zoneIds, kitIds, directCaseIds, cohortIds] = await Promise.all([
    selectIds('aql_locations', 'location_id', 'client_id', clientId),
    selectIds('aql_zones', 'zone_id', 'client_id', clientId),
    selectIds('aql_kits', 'kit_id', 'client_id', clientId),
    selectIds('aql_cases', 'case_id', 'client_id', clientId),
    selectIds('aql_cohorts', 'cohort_id', 'client_id', clientId)
  ]);

  const kitIdsFromLocations = await selectIdsIn('aql_kits', 'kit_id', 'location_id', locationIds);
  const allKitIds = Array.from(new Set([...kitIds, ...kitIdsFromLocations]));
  const [cameraIdsByKit, cameraIdsByZone, sensorIds, deviceIds] = await Promise.all([
    selectIdsIn('aql_cameras', 'camera_id', 'kit_id', allKitIds),
    selectIdsIn('aql_cameras', 'camera_id', 'zone_id', zoneIds),
    selectIdsIn('aql_sensors', 'sensor_id', 'kit_id', allKitIds),
    selectIdsIn('aql_devices', 'device_id', 'kit_id', allKitIds)
  ]);
  const cameraIds = Array.from(new Set([...cameraIdsByKit, ...cameraIdsByZone]));
  const sensorDeviceIds = await selectIdsIn('aql_sensors', 'device_id', 'sensor_id', sensorIds);
  const allDeviceIds = Array.from(new Set([...deviceIds, ...sensorDeviceIds]));
  const captureIds = await selectIdsIn('aql_vision_captures', 'capture_id', 'device_id', allDeviceIds);
  const detectionIds = await selectIdsIn('aql_detections', 'detection_id', 'capture_id', captureIds);
  const [alertIdsByDevice, alertIdsByCamera, alertIdsByZone] = await Promise.all([
    selectIdsIn('aql_alerts', 'alert_id', 'device_id', allDeviceIds),
    selectIdsIn('aql_alerts', 'alert_id', 'camera_id', cameraIds),
    selectIdsIn('aql_alerts', 'alert_id', 'zone_id', zoneIds)
  ]);
  const directAlertIds = Array.from(new Set([...alertIdsByDevice, ...alertIdsByCamera, ...alertIdsByZone]));

  await deleteIn('aql_cases', 'detection_id', detectionIds);
  await deleteIn('aql_cases', 'source_capture_id', captureIds);
  await deleteIn('aql_cases', 'alert_id', directAlertIds);
  await deleteIn('aql_cases', 'case_id', directCaseIds);
  await deleteEq('aql_cases', 'client_id', clientId);

  await deleteEq('aql_growth_measurements', 'client_id', clientId);
  await deleteIn('aql_growth_measurements', 'cohort_id', cohortIds);
  await deleteIn('aql_growth_measurements', 'capture_id', captureIds);
  await deleteEq('aql_growth_calibrations', 'client_id', clientId);
  await deleteIn('aql_growth_calibrations', 'kit_id', allKitIds);
  await deleteEq('aql_cohorts', 'client_id', clientId);

  await deleteEq('aql_biomass_metrics', 'client_id', clientId);
  await deleteEq('aql_growth_metrics', 'client_id', clientId);
  await deleteEq('aql_growth_expected_curves', 'client_id', clientId);

  await deleteIn('aql_detections', 'detection_id', detectionIds);
  await deleteIn('aql_vision_captures', 'capture_id', captureIds);
  await deleteIn('aql_sensor_readings', 'device_id', allDeviceIds);
  await deleteEq('aql_sensor_readings', 'client_id', clientId);
  await deleteIn('aql_alerts', 'device_id', allDeviceIds);
  await deleteIn('aql_alerts', 'camera_id', cameraIds);
  await deleteIn('aql_alerts', 'zone_id', zoneIds);

  await deleteEq('aql_project_settings', 'client_id', clientId);
  await deleteEq('aql_user_clients', 'client_id', clientId);
  await deleteEq('aql_push_subscriptions', 'client_id', clientId);
  await deleteEq('aql_users', 'client_id', clientId);
  await deleteEq('aql_species', 'client_id', clientId);
  await deleteEq('aql_phases', 'client_id', clientId);
  await deleteIn('aql_sensors', 'sensor_id', sensorIds);
  await deleteIn('aql_devices', 'device_id', allDeviceIds);
  await deleteIn('aql_cameras', 'camera_id', cameraIds);
  await deleteIn('aql_kits', 'kit_id', allKitIds);
  await deleteIn('aql_zones', 'zone_id', zoneIds);
  await deleteIn('aql_locations', 'location_id', locationIds);

  await deleteConfigurationRecord('clients', client);
}
